import { useState } from "react";
import axios from "axios";
import { motion } from "framer-motion";
import { Send, Loader2, Phone } from "lucide-react";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const SERVICE_OPTIONS = [
  "Water Damage",
  "Fire & Smoke Damage",
  "Mold Remediation",
  "Sewage Cleanup",
  "Biohazard Cleanup",
  "Home Rebuild",
  "Other / Not Sure",
];

const URGENCY_OPTIONS = [
  { value: "emergency", label: "Emergency Now" },
  { value: "urgent", label: "Within Days" },
  { value: "standard", label: "Planning Ahead" },
];

const inputClass =
  "w-full rounded-xl border border-white/10 bg-[#0D0D12] px-4 py-3.5 text-sm text-slate-100 placeholder-slate-600 outline-none transition-all duration-300 focus:border-purple-400/60 focus:ring-2 focus:ring-purple-500/20";

const initialForm = {
  name: "",
  phone: "",
  email: "",
  service_type: "Water Damage",
  property_address: "",
  urgency: "standard",
  message: "",
};

export default function RequestForm() {
  const [form, setForm] = useState(initialForm);
  const [submitting, setSubmitting] = useState(false);

  const update = (key) => (e) => setForm((f) => ({ ...f, [key]: e.target.value }));

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      const payload = { ...form };
      if (!payload.email) delete payload.email;
      if (!payload.property_address) delete payload.property_address;
      if (!payload.message) delete payload.message;
      await axios.post(`${API}/service-requests`, payload);
      toast.success("Request received.", {
        description: "A Redemption Restoration specialist will call you back shortly.",
      });
      setForm(initialForm);
    } catch (err) {
      toast.error("Something went wrong.", {
        description: "Please try again, or call us directly at 920-415-2145.",
      });
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <section id="request-service" className="relative overflow-hidden py-24 lg:py-32" data-testid="request-form-section">
      <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_bottom,rgba(139,92,246,0.12),transparent_60%)]" />
      <div className="relative mx-auto grid max-w-7xl grid-cols-1 gap-14 px-4 sm:px-6 lg:grid-cols-5 lg:gap-20 lg:px-8">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-80px" }}
          transition={{ duration: 0.8, ease: [0.215, 0.61, 0.355, 1] }}
          className="lg:col-span-2"
        >
          <p className="font-mono-data mb-4 text-xs uppercase tracking-[0.35em] text-purple-400">
            Request Service
          </p>
          <h2 className="font-serif-display text-3xl font-medium leading-tight tracking-tight text-slate-100 sm:text-4xl lg:text-5xl">
            Tell us what happened. <span className="italic text-purple-400">We handle the rest.</span>
          </h2>
          <p className="mt-6 text-base font-light leading-relaxed text-slate-400">
            Fill out the form and a specialist will call you back to schedule an assessment. For
            emergencies, skip the form — call now and we dispatch immediately.
          </p>
          <a
            href="tel:9204152145"
            data-testid="form-call-link"
            className="group mt-8 inline-flex items-center gap-4 rounded-2xl border border-purple-500/25 bg-purple-500/[0.07] px-6 py-5 transition-all duration-300 hover:border-purple-400/50 hover:bg-purple-500/[0.12]"
          >
            <span className="flex h-11 w-11 items-center justify-center rounded-full bg-purple-600 text-white shadow-[0_0_24px_rgba(139,92,246,0.45)] transition-transform duration-300 group-hover:scale-110">
              <Phone className="h-5 w-5" />
            </span>
            <span>
              <span className="font-mono-data block text-[10px] uppercase tracking-[0.25em] text-purple-300">
                24/7 Emergency
              </span>
              <span className="font-mono-data block text-lg font-semibold tracking-wider text-white">
                920-415-2145
              </span>
            </span>
          </a>
        </motion.div>

        <motion.form
          onSubmit={handleSubmit}
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-80px" }}
          transition={{ delay: 0.15, duration: 0.8, ease: [0.215, 0.61, 0.355, 1] }}
          data-testid="service-request-form"
          className="rounded-3xl border border-white/[0.07] bg-[#111116]/80 p-7 backdrop-blur sm:p-10 lg:col-span-3"
        >
          <div className="grid grid-cols-1 gap-5 sm:grid-cols-2">
            <div>
              <label htmlFor="req-name" className="font-mono-data mb-2 block text-[10px] uppercase tracking-[0.22em] text-slate-500">
                Full Name *
              </label>
              <input
                id="req-name"
                data-testid="form-name-input"
                required
                value={form.name}
                onChange={update("name")}
                placeholder="Jane Smith"
                className={inputClass}
              />
            </div>
            <div>
              <label htmlFor="req-phone" className="font-mono-data mb-2 block text-[10px] uppercase tracking-[0.22em] text-slate-500">
                Phone *
              </label>
              <input
                id="req-phone"
                data-testid="form-phone-input"
                required
                type="tel"
                value={form.phone}
                onChange={update("phone")}
                placeholder="(920) 555-0123"
                className={inputClass}
              />
            </div>
            <div>
              <label htmlFor="req-email" className="font-mono-data mb-2 block text-[10px] uppercase tracking-[0.22em] text-slate-500">
                Email
              </label>
              <input
                id="req-email"
                data-testid="form-email-input"
                type="email"
                value={form.email}
                onChange={update("email")}
                placeholder="you@email.com"
                className={inputClass}
              />
            </div>
            <div>
              <label htmlFor="req-service" className="font-mono-data mb-2 block text-[10px] uppercase tracking-[0.22em] text-slate-500">
                Service Needed *
              </label>
              <select
                id="req-service"
                data-testid="form-service-select"
                required
                value={form.service_type}
                onChange={update("service_type")}
                className={`${inputClass} appearance-none`}
              >
                {SERVICE_OPTIONS.map((opt) => (
                  <option key={opt} value={opt} className="bg-[#111116]">
                    {opt}
                  </option>
                ))}
              </select>
            </div>
            <div className="sm:col-span-2">
              <label htmlFor="req-address" className="font-mono-data mb-2 block text-[10px] uppercase tracking-[0.22em] text-slate-500">
                Property Address
              </label>
              <input
                id="req-address"
                data-testid="form-address-input"
                value={form.property_address}
                onChange={update("property_address")}
                placeholder="123 Main St, Appleton, WI"
                className={inputClass}
              />
            </div>
            <div className="sm:col-span-2">
              <p className="font-mono-data mb-2 text-[10px] uppercase tracking-[0.22em] text-slate-500">
                How Urgent Is It?
              </p>
              <div className="flex flex-wrap gap-2" data-testid="form-urgency-group">
                {URGENCY_OPTIONS.map((opt) => (
                  <button
                    key={opt.value}
                    type="button"
                    data-testid={`form-urgency-${opt.value}`}
                    onClick={() => setForm((f) => ({ ...f, urgency: opt.value }))}
                    className={`rounded-full border px-4 py-2 text-xs font-semibold tracking-wide transition-all duration-300 ${
                      form.urgency === opt.value
                        ? "border-purple-400 bg-purple-600/20 text-purple-200 shadow-[0_0_16px_rgba(139,92,246,0.3)]"
                        : "border-white/10 text-slate-400 hover:border-slate-400/40 hover:text-slate-200"
                    }`}
                  >
                    {opt.label}
                  </button>
                ))}
              </div>
            </div>
            <div className="sm:col-span-2">
              <label htmlFor="req-message" className="font-mono-data mb-2 block text-[10px] uppercase tracking-[0.22em] text-slate-500">
                What Happened?
              </label>
              <textarea
                id="req-message"
                data-testid="form-message-input"
                rows={4}
                value={form.message}
                onChange={update("message")}
                placeholder="Briefly describe the damage or situation..."
                className={`${inputClass} resize-none`}
              />
            </div>
          </div>
          <button
            type="submit"
            disabled={submitting}
            data-testid="service-request-form-submit-button"
            className="group mt-8 flex w-full items-center justify-center gap-3 rounded-full bg-purple-600 px-8 py-4 text-sm font-semibold uppercase tracking-widest text-white shadow-[0_0_36px_rgba(139,92,246,0.35)] transition-all duration-300 hover:bg-purple-500 hover:shadow-[0_0_56px_rgba(139,92,246,0.55)] disabled:cursor-not-allowed disabled:opacity-60"
          >
            {submitting ? (
              <Loader2 className="h-4 w-4 animate-spin" />
            ) : (
              <Send className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-1" />
            )}
            {submitting ? "Sending..." : "Send Service Request"}
          </button>
          <p className="mt-4 text-center text-xs text-slate-600">
            No obligation. We respond to every request — usually within the hour.
          </p>
        </motion.form>
      </div>
    </section>
  );
}
